import React, { useEffect } from 'react';
import FileSaver from 'file-saver';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../reducers';
import { fetchAdoptionImportLogs } from '../../../actions/receiver/adoption';
import ImportExportLogs from '../import_export/ImportExportLogs';
import { dateToString } from '../parentSettings/importLogs/ImportExportUtils';
import { AsyncDispatch } from '../../../../types/global';
import { getPageLimit } from '../../../utils/Utils';
import {
  fetchImportLogDetailsJSON,
  fetchImportLogsDetailsExcel,
  fetchImport,
} from '../../../actions/brand/import_export/fetch';
import { useAppDispatch } from '../../../store/ReduxStore';

const AdoptionImportReportsTab: React.FC = () => {
  const dispatch: AsyncDispatch = useAppDispatch();

  const { adoptionImportLogs, adoptionImportLogsLoading, importErrorLogDetail } = useSelector(
    (state: ApplicationState) => ({
      adoptionImportLogs: state.receiver.receivers.adoptionImportLogs,
      adoptionImportLogsLoading: state.receiver.receivers.adoptionImportLogsLoading,
      importErrorLogDetail: state.brand.importExport?.importErrorLogDetail || [],
    })
  );

  const [pageSize] = React.useState<number>(getPageLimit());

  const formattedAdoptionLogs = React.useMemo(() => {
    if (!adoptionImportLogs) return [];
    return adoptionImportLogs.map(log => ({
      ...log,
      startedTime: dateToString(log.started_at),
      completedTime: dateToString(log.completed_at),
      fileTypeName: '', // Not needed since hideFileType=true
    }));
  }, [adoptionImportLogs]);

  useEffect(() => {
    dispatch(fetchAdoptionImportLogs({ limit: pageSize }));
  }, [dispatch, pageSize]);

  const saveImportLogsDetailsFile = ({ errorLogFile }: { errorLogFile: any }) => {
    const blob = new Blob([errorLogFile], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, 'adoption_import_errors.xlsx');
  };

  const handleErrorDownload = (logId: number) =>
    dispatch(fetchImportLogsDetailsExcel(logId) as any).then((response: any) =>
      saveImportLogsDetailsFile({ errorLogFile: response.action.payload.data })
    );

  const handleImportFileDownload = (logId: number) => dispatch(fetchImport(logId) as any);

  const handleFetchImportLogDetailsJSON = (logId: number) =>
    dispatch(fetchImportLogDetailsJSON(logId) as any);

  const getImportLogs = (page: number) => {
    return dispatch(
      fetchAdoptionImportLogs({
        limit: pageSize,
        page,
      })
    );
  };

  return (
    <ImportExportLogs
      logs={formattedAdoptionLogs}
      fetchingLogs={adoptionImportLogsLoading}
      fetchingErrors={false}
      errorLogDetails={importErrorLogDetail}
      handleErrorDownload={handleErrorDownload}
      handleFileDownload={handleImportFileDownload}
      handleFetchLogDetailsJSON={handleFetchImportLogDetailsJSON}
      pageSize={pageSize}
      fetchLogs={getImportLogs}
      hideFileType
      hideApplicationCount
      hideItemCount
    />
  );
};

export default AdoptionImportReportsTab;
